import { BrandLogo, SidebarGroup } from '@catering/ui';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import type { CurrentUser } from '../auth/use-current-user';
import { GUIDE_ITEM, NAVIGATION_GROUPS, visibleNavGroups } from './navigation';

const NAV_ITEM_CLASSNAME =
  'flex items-center gap-3 rounded-md border-l-2 border-transparent px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-primary-50 hover:text-primary-700';
const NAV_ITEM_ACTIVE_CLASSNAME = 'border-primary-500 bg-primary-50 font-semibold text-primary-700';

export interface SidebarNavProps {
  currentUser: CurrentUser | undefined;
  onNavigate?: () => void;
}

export function SidebarNav({ currentUser, onNavigate }: SidebarNavProps): JSX.Element {
  const groups = visibleNavGroups(NAVIGATION_GROUPS, currentUser);
  const GuideIcon = GUIDE_ITEM.icon;

  return (
    <nav
      aria-label="Principal"
      className="flex h-full w-64 shrink-0 flex-col border-r border-slate-200 bg-white"
    >
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2 px-5 py-5">
        <BrandLogo size="md" />
        <span className="text-xs font-medium text-slate-500">Back-office</span>
      </Link>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-3 pb-4">
        {groups.map((group) => (
          <SidebarGroup key={group.id} label={group.label}>
            {group.items.map((item) => {
              const Icon = item.icon;

              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onNavigate}
                    activeOptions={{ exact: item.to === '/' }}
                    className={NAV_ITEM_CLASSNAME}
                    activeProps={{ className: NAV_ITEM_ACTIVE_CLASSNAME }}
                  >
                    <Icon aria-hidden="true" className="size-[18px] shrink-0" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </SidebarGroup>
        ))}
      </div>

      <ul className="border-t border-slate-200 px-3 py-3">
        <li>
          <Link
            to={GUIDE_ITEM.to}
            onClick={onNavigate}
            className={NAV_ITEM_CLASSNAME}
            activeProps={{ className: NAV_ITEM_ACTIVE_CLASSNAME }}
          >
            <GuideIcon aria-hidden="true" className="size-[18px] shrink-0" />
            {GUIDE_ITEM.label}
          </Link>
        </li>
      </ul>
    </nav>
  );
}
